import React, { useMemo, useState } from 'react';
import { ChartFilter } from '../ChartComponents';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { SelectedItemProps } from '../MattersByStatusChart';
import {
    FormControl,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    Theme,
    createStyles,
    makeStyles,
} from '@material-ui/core';
import { ChartInfo } from '../../components/ChartInfo';
import MTooltip from '@material-ui/core/Tooltip';
import { VideoModal } from '../../components/VideoModal';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import CloseIcon from '@material-ui/icons/Close';
import { useScreenshot, createFileName } from 'use-react-screenshot';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import { RV_CHART_CLASS, RV_CHART_COLORS } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { formatNumber } from '../../helpers/ChartHelper';
import clsx from 'clsx';
import { SearchCriteria } from '../SearchCriteria';
import { DrillDownType } from '../DrillDownType';
import { useCurrencySymbol } from '../../hooks/useCurrencySymbol';
import html2canvas from 'html2canvas';
import { AIChartDialog } from '../../components/Dialogs/AIChartDialog';
import { AIIcon } from '../../components/icons/AIIcon';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        rootLoader: {
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
        },
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        learnIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 0 15px',
        },
        title: {
            fontSize: '2.5rem',
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        content: {
            padding: '0 15px 8px 15px',
            flex: '1 1 auto',
            display: 'flex',
            flexFlow: 'column',
            textAlign: 'left',
        },
        eyebrow: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            margin: '12px 0 8px 0',
            fontSize: '11px',
            fontWeight: 800,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            color: 'var(--rv-chart-muted)',
            '&:first-child': {
                marginTop: 0,
            },
            '&::after': {
                content: '""',
                flex: 1,
                height: '1px',
                backgroundColor: 'var(--rv-chart-line)',
            },
        },
        tiles: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: '8px',
        },
        tile: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            minWidth: 0,
            padding: '10px 12px',
            border: '1px solid var(--rv-chart-line)',
            borderRadius: '10px',
            background: 'linear-gradient(180deg, #fff 0%, #fafbff 100%)',
        },
        tileText: {
            flex: 1,
            minWidth: 0,
        },
        tileName: {
            fontSize: '10.5px',
            fontWeight: 800,
            letterSpacing: '0.06em',
            lineHeight: 1.3,
            textTransform: 'uppercase',
            color: 'var(--rv-chart-muted)',
        },
        tileActual: {
            marginTop: '1px',
            fontSize: '19px',
            fontWeight: 800,
            letterSpacing: '-0.6px',
            lineHeight: 1.25,
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--rv-chart-ink)',
        },
        tileBudget: {
            minHeight: '17px',
            fontSize: '11.5px',
            color: 'var(--rv-chart-muted)',
            fontVariantNumeric: 'tabular-nums',
        },
        tileEmpty: {
            color: 'var(--rv-chart-faint)',
        },
        ring: {
            position: 'relative',
            width: '46px',
            height: '46px',
            flex: '0 0 auto',
            '& svg': {
                transform: 'rotate(-90deg)',
            },
        },
        ringProgress: {
            transition: 'stroke-dashoffset 0.8s cubic-bezier(0.2, 0.8, 0.2, 1)',
            '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
            },
        },
        ringLabel: {
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            fontWeight: 800,
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--rv-chart-ink)',
        },
        ageingRow: {
            padding: '8px 0',
            borderBottom: '1px solid var(--rv-chart-line)',
            '&:last-child': {
                borderBottom: 'none',
            },
        },
        ageingTop: {
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: '10px',
        },
        ageingName: {
            fontSize: '13px',
            fontWeight: 600,
            color: 'var(--rv-chart-muted)',
        },
        ageingTotal: {
            fontSize: '16px',
            fontWeight: 800,
            letterSpacing: '-0.3px',
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--rv-chart-ink)',
        },
        ageingTrack: {
            height: '8px',
            margin: '6px 0 4px 0',
            borderRadius: '999px',
            overflow: 'hidden',
            backgroundColor: 'var(--rv-chart-track)',
        },
        ageingFill: {
            height: '100%',
            borderRadius: '999px',
            transition: 'width 0.8s cubic-bezier(0.2, 0.8, 0.2, 1)',
            '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
            },
        },
        ageingText: {
            fontSize: '12px',
            color: 'var(--rv-chart-muted)',
            fontVariantNumeric: 'tabular-nums',
        },
        pointer: {
            cursor: 'pointer',
        },
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        table: {},
        tableHeaderCell: {
            fontWeight: 600
        }
    }),
);

type KPISummaryDataType = 'New Matters' | 'Non-Billable Hours' | 'Billable Hours' | 'Fees Recorded' | 'Fees Billed' | 'Fees Collected';

type KPISummaryToDateType = {
    actual: number | string | null;
    budget: number | string | null;
    percentage: number | string | null;
    // Red/Amber/Green
    dot?: 'red' | 'orange' | 'green';
};

type KPISummaryData = {
    name: KPISummaryDataType;
    weekToDate: KPISummaryToDateType;
    monthToDate: KPISummaryToDateType;
    yearToDate: KPISummaryToDateType;
    previousMonthToDate?: KPISummaryToDateType;
    previousMonthYearToDate?: KPISummaryToDateType;
    forecast?: KPISummaryToDateType;
};

type KPISummaryBottomData = {
    name: 'Total Debt' | 'Unbilled Fees' | 'Unbilled Disbursements';
    data: Total | null;
    exceedPercentage: number | null;
};

type DateOption = keyof Omit<KPISummaryData, 'name'>;
interface KPISummaryChartChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    infoUrl: string | null;
}

export const KPISummaryChart = (props: KPISummaryChartChartProps) => {
    const classes = useStyles();

    const chartName = 'KPI Summary';

    // tslint:disable-next-line: no-any
    const entireChartRef = React.useRef<any>();

    const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);

    const kpiSummaryQuery = useQuery<KpiSummaryData, KpiSummaryDataParams>(KPISummaryQuery, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility
        }
    });

    const [options, setOptions] = useState<DateOption>('weekToDate');

    const [data, setData] = useState<KPISummaryData[]>([]);

    const [bottomData, setBottomData] = useState<KPISummaryBottomData[]>([]);

    const [currencySymbol, setCurrencySymbol] = useState<string>('$');

    const currencyQuery = useCurrencySymbol();

    const [base64Data, setBase64Data] = useState<string | ArrayBuffer | null>(null);

    const [isAIDialogOpen, setIsAIDialogOpen] = useState<boolean>(false);

    const [isBlobGenerating, setIsBlobGenerating] = useState<boolean>(false);

    const takeScreenShot = useScreenshot({
        type: 'image/jpeg',
        quality: 1.0,
    })[1];

    const  getIndicator = (percentage: number, amberIndicatorWhenActualVsBudgetIsLessThanPercentage: number | null, redIndicatorWhenActualVsBudgetIsLessThanPercentage: number | null) => {
        if (!percentage || Math.round(percentage) === 0 || !amberIndicatorWhenActualVsBudgetIsLessThanPercentage || !redIndicatorWhenActualVsBudgetIsLessThanPercentage) {
            return undefined;
        }
        if (Math.round(percentage) <= redIndicatorWhenActualVsBudgetIsLessThanPercentage) {
            return 'red';
        } else if (Math.round(percentage) < amberIndicatorWhenActualVsBudgetIsLessThanPercentage) {
            return 'orange';
        } else {
            return 'green'
        }
    };

    useMemo(() => {
        if (!currencyQuery.loading && !currencyQuery.error && currencyQuery.data) {
            setCurrencySymbol(currencyQuery.data.options.currencySymbol.symbol);
        }
    // tslint:disable-next-line: align
    }, [currencyQuery.loading, currencyQuery.data, currencyQuery.error]);

    useMemo(() => {
        if (!kpiSummaryQuery.loading && !kpiSummaryQuery.error && kpiSummaryQuery.data) {

            const {
                amberIndicatorWhenActualVsBudgetIsLessThanPercentage,
                redIndicatorWhenActualVsBudgetIsLessThanPercentage,
                redIndicatorWhenNinetyDayDebtExceedsPercentage,
                redIndicatorWhenNinetyDayUnbilledDisbursementsExceedsPercentage,
                redIndicatorWhenNinetyDayUnbilledFeesExceedsPercentage
            } = kpiSummaryQuery.data.settings.systems.financialThresholdsSettings.config

            const {
                newMattersMTD,
                newMattersYTD,
                nonBillableHoursMTD,
                nonBillableHoursYTD,
                billableHoursMTD,
                billableHoursYTD,
                feesRecordedMTD,
                feesRecordedYTD,
                feesBilledMTD,
                feesBilledYTD,
                feesCollectedMTD,
                feesCollectedYTD,
                billableHoursPreviousMTD,
                billableHoursPreviousMYTD,
                feesRecordedPreviousMTD,
                feesBilledPreviousMTD,
                feesBilledPreviousMYTD,
                feesCollectedPreviousMTD,
                feesCollectedPreviousMYTD,
                feesRecordedPreviousMYTD,
                newMattersPreviousMTD,
                newMattersPreviousMYTD,
                nonBillableHoursPreviousMTD,
                nonBillableHoursPreviousMYTD,
                totalDebtOwing,
                totalUnbilledDisbursements,
                totalUnbilledFees,
                feesCollectedForecast,
                feesRecordedForecast,
                feesBilledForecast,
                billableHoursWTD,
                feesBilledWTD,
                feesCollectedWTD,
                feesRecordedWTD,
                newMattersWTD,
                nonBillableHoursWTD
            } = kpiSummaryQuery.data.statistics.fees.kpiSummary;
            const tempData: KPISummaryData[] = [];
            const tempBottomData: KPISummaryBottomData[] = [];

            // bottom data
            if (totalDebtOwing) {
                tempBottomData.push({
                    name: 'Total Debt',
                    data: totalDebtOwing,
                    exceedPercentage: redIndicatorWhenNinetyDayDebtExceedsPercentage
                });
            }

            if (totalUnbilledFees) {
                tempBottomData.push({
                    name: 'Unbilled Fees',
                    data: totalUnbilledFees,
                    exceedPercentage: redIndicatorWhenNinetyDayUnbilledFeesExceedsPercentage
                });
            }

            if (totalUnbilledDisbursements) {
                tempBottomData.push({
                    name: 'Unbilled Disbursements',
                    data: totalUnbilledDisbursements,
                    exceedPercentage: redIndicatorWhenNinetyDayUnbilledDisbursementsExceedsPercentage
                });
            }

            // new matters
            tempData.push({
                name: 'New Matters',
                monthToDate: {
                    actual: newMattersMTD.periodTotal && newMattersMTD.periodTotal > 0 ? formatNumber(newMattersMTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                yearToDate: {
                    actual: newMattersYTD.periodTotal && newMattersYTD.periodTotal > 0 ? formatNumber(newMattersYTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                previousMonthToDate: {
                    actual: newMattersPreviousMTD.periodTotal && newMattersPreviousMTD.periodTotal > 0 ? formatNumber(newMattersPreviousMTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                previousMonthYearToDate: {
                    actual: newMattersPreviousMYTD.periodTotal && newMattersPreviousMYTD.periodTotal > 0 ? formatNumber(newMattersPreviousMYTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                forecast: {
                    actual: newMattersYTD.periodTotal && newMattersYTD.periodTotal > 0 ? formatNumber(newMattersYTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                weekToDate: {
                    actual: newMattersWTD.periodTotal && newMattersWTD.periodTotal > 0 ? formatNumber(newMattersWTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
            });

            // billable hours add dollar to acutal and budget
            tempData.push({
                name: 'Billable Hours',
                monthToDate: {
                    actual: billableHoursMTD.periodTotal && billableHoursMTD.periodTotal > 0 ? `${formatNumber(billableHoursMTD.periodTotal)}` : null,
                    budget: billableHoursMTD.budget && billableHoursMTD.budget > 0 ? `${formatNumber(billableHoursMTD.budget)}` : null,
                    percentage: billableHoursMTD.percentage && billableHoursMTD.percentage > 0 ? `${formatNumber(billableHoursMTD.percentage)}%` : null,
                    dot: getIndicator(billableHoursMTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                yearToDate: {
                    actual: billableHoursYTD.periodTotal && billableHoursYTD.periodTotal > 0 ? `${formatNumber(billableHoursYTD.periodTotal)}` : null,
                    budget: billableHoursYTD.budget && billableHoursYTD.budget > 0 ? `${formatNumber(billableHoursYTD.budget)}` : null,
                    percentage: billableHoursYTD.percentage && billableHoursYTD.percentage > 0 ? `${formatNumber(billableHoursYTD.percentage)}%` : null,
                    dot: getIndicator(billableHoursYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                previousMonthToDate: {
                    actual: billableHoursPreviousMTD.periodTotal && billableHoursPreviousMTD.periodTotal > 0 ? `${formatNumber(billableHoursPreviousMTD.periodTotal)}` : null,
                    budget: billableHoursPreviousMTD.budget && billableHoursPreviousMTD.budget > 0 ? `${formatNumber(billableHoursPreviousMTD.budget)}` : null,
                    percentage: billableHoursPreviousMTD.percentage && billableHoursPreviousMTD.percentage > 0 ? `${formatNumber(billableHoursPreviousMTD.percentage)}%` : null,
                    dot: getIndicator(billableHoursPreviousMTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                previousMonthYearToDate: {
                    actual: billableHoursPreviousMYTD.periodTotal && billableHoursPreviousMYTD.periodTotal > 0 ? `${formatNumber(billableHoursPreviousMYTD.periodTotal)}` : null,
                    budget: billableHoursPreviousMYTD.budget && billableHoursPreviousMYTD.budget > 0 ? `${formatNumber(billableHoursPreviousMYTD.budget)}` : null,
                    percentage: billableHoursPreviousMYTD.percentage && billableHoursPreviousMYTD.percentage > 0 ? `${formatNumber(billableHoursPreviousMYTD.percentage)}%` : null,
                    dot: getIndicator(billableHoursPreviousMYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                forecast: {
                    actual: billableHoursYTD.periodTotal && billableHoursYTD.periodTotal > 0 ? `${formatNumber(billableHoursYTD.periodTotal)}` : null,
                    budget: billableHoursYTD.budget && billableHoursYTD.budget > 0 ? `${formatNumber(billableHoursYTD.budget)}` : null,
                    percentage: billableHoursYTD.percentage && billableHoursYTD.percentage > 0 ? `${formatNumber(billableHoursYTD.percentage)}%` : null,
                    dot: getIndicator(billableHoursYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                weekToDate: {
                    actual: billableHoursWTD.periodTotal && billableHoursWTD.periodTotal > 0 ? `${formatNumber(billableHoursWTD.periodTotal)}` : null,
                    budget: billableHoursWTD.budget && billableHoursWTD.budget > 0 ? `${formatNumber(billableHoursWTD.budget)}` : null,
                    percentage: billableHoursWTD.percentage && billableHoursWTD.percentage > 0 ? `${formatNumber(billableHoursWTD.percentage)}%` : null,
                }
            });

            // // fees recorded
            tempData.push({
                name: 'Fees Recorded',
                monthToDate: {
                    actual: feesRecordedMTD.periodTotal && feesRecordedMTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesRecordedMTD.periodTotal)}` : null,
                    budget: feesRecordedMTD.budget && feesRecordedMTD.budget > 0 ? `${currencySymbol}${formatNumber(feesRecordedMTD.budget)}` : null,
                    percentage: feesRecordedMTD.percentage && feesRecordedMTD.percentage > 0 ? `${formatNumber(feesRecordedMTD.percentage)}%` : null,
                    dot: getIndicator(feesRecordedMTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                yearToDate: {
                    actual: feesRecordedYTD.periodTotal && feesRecordedYTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesRecordedYTD.periodTotal)}` : null,
                    budget: feesRecordedYTD.budget && feesRecordedYTD.budget > 0 ? `${currencySymbol}${formatNumber(feesRecordedYTD.budget)}` : null,
                    percentage: feesRecordedYTD.percentage && feesRecordedYTD.percentage > 0 ? `${formatNumber(feesRecordedYTD.percentage)}%` : null,
                    dot: getIndicator(feesRecordedYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                previousMonthToDate: {
                    actual:
                        feesRecordedPreviousMTD.periodTotal && feesRecordedPreviousMTD.periodTotal > 0
                            ? `${currencySymbol}${formatNumber(feesRecordedPreviousMTD.periodTotal)}`
                            : null,
                    budget: feesRecordedPreviousMTD.budget && feesRecordedPreviousMTD.budget > 0 ? `${currencySymbol}${formatNumber(feesRecordedPreviousMTD.budget)}` : null,
                    percentage: feesRecordedPreviousMTD.percentage && feesRecordedPreviousMTD.percentage > 0 ? `${formatNumber(feesRecordedPreviousMTD.percentage)}%` : null,
                    dot: getIndicator(feesRecordedPreviousMTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                previousMonthYearToDate: {
                    actual:
                        feesRecordedPreviousMYTD.periodTotal && feesRecordedPreviousMYTD.periodTotal > 0
                            ? `${currencySymbol}${formatNumber(feesRecordedPreviousMYTD.periodTotal)}`
                            : null,
                    budget: feesRecordedPreviousMYTD.budget && feesRecordedPreviousMYTD.budget > 0 ? `${currencySymbol}${formatNumber(feesRecordedPreviousMYTD.budget)}` : null,
                    percentage: feesRecordedPreviousMYTD.percentage && feesRecordedPreviousMYTD.percentage > 0 ? `${formatNumber(feesRecordedPreviousMYTD.percentage)}%` : null,
                    dot: getIndicator(feesRecordedPreviousMYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                forecast: {
                    actual: feesRecordedForecast.periodTotal && feesRecordedForecast.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesRecordedForecast.periodTotal)}` : null,
                    budget: feesRecordedForecast.budget && feesRecordedForecast.budget > 0 ? `${currencySymbol}${formatNumber(feesRecordedForecast.budget)}` : null,
                    percentage: feesRecordedForecast.percentage && feesRecordedForecast.percentage > 0 ? `${formatNumber(feesRecordedForecast.percentage)}%` : null,
                    dot: getIndicator(feesRecordedForecast.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                weekToDate: {
                    actual: feesRecordedWTD.periodTotal && feesRecordedWTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesRecordedWTD.periodTotal)}` : null,
                    budget: feesRecordedWTD.budget && feesRecordedWTD.budget > 0 ? `${currencySymbol}${formatNumber(feesRecordedWTD.budget)}` : null,
                    percentage: feesRecordedWTD.percentage && feesRecordedWTD.percentage > 0 ? `${formatNumber(feesRecordedWTD.percentage)}%` : null,
                    dot: getIndicator(feesRecordedWTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                }
            });

            // fees billed
            tempData.push({
                name: 'Fees Billed',
                monthToDate: {
                    actual: feesBilledMTD.periodTotal && feesBilledMTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesBilledMTD.periodTotal)}` : null,
                    budget: feesBilledMTD.budget && feesBilledMTD.budget > 0 ? `${currencySymbol}${formatNumber(feesBilledMTD.budget)}` : null,
                    percentage: feesBilledMTD.percentage && feesBilledMTD.percentage > 0 ? `${formatNumber(feesBilledMTD.percentage)}%` : null,
                    dot: getIndicator(feesBilledMTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                yearToDate: {
                    actual: feesBilledYTD.periodTotal && feesBilledYTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesBilledYTD.periodTotal)}` : null,
                    budget: feesBilledYTD.budget && feesBilledYTD.budget > 0 ? `${currencySymbol}${formatNumber(feesBilledYTD.budget)}` : null,
                    percentage: feesBilledYTD.percentage && feesBilledYTD.percentage > 0 ? `${formatNumber(feesBilledYTD.percentage)}%` : null,
                    dot: getIndicator(feesBilledYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                previousMonthToDate: {
                    actual:
                        feesBilledPreviousMTD.periodTotal && feesBilledPreviousMTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesBilledPreviousMTD.periodTotal)}` : null,
                    budget: feesBilledPreviousMTD.budget && feesBilledPreviousMTD.budget > 0 ? `${currencySymbol}${formatNumber(feesBilledPreviousMTD.budget)}` : null,
                    percentage: feesBilledPreviousMTD.percentage && feesBilledPreviousMTD.percentage > 0 ? `${formatNumber(feesBilledPreviousMTD.percentage)}%` : null,
                    dot: getIndicator(feesBilledPreviousMTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                previousMonthYearToDate: {
                    actual:
                        feesBilledPreviousMYTD.periodTotal && feesBilledPreviousMYTD.periodTotal > 0
                            ? `${currencySymbol}${formatNumber(feesBilledPreviousMYTD.periodTotal)}`
                            : null,
                    budget: feesBilledPreviousMYTD.budget && feesBilledPreviousMYTD.budget > 0 ? `${currencySymbol}${formatNumber(feesBilledPreviousMYTD.budget)}` : null,
                    percentage: feesBilledPreviousMYTD.percentage && feesBilledPreviousMYTD.percentage > 0 ? `${formatNumber(feesBilledPreviousMYTD.percentage)}%` : null,
                    dot: getIndicator(feesBilledPreviousMYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                forecast: {
                    actual: feesBilledForecast.periodTotal && feesBilledForecast.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesBilledForecast.periodTotal)}` : null,
                    budget: feesBilledForecast.budget && feesBilledForecast.budget > 0 ? `${currencySymbol}${formatNumber(feesBilledForecast.budget)}` : null,
                    percentage: feesBilledForecast.percentage && feesBilledForecast.percentage > 0 ? `${formatNumber(feesBilledForecast.percentage)}%` : null,
                    dot: getIndicator(feesBilledForecast.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                weekToDate: {
                    actual: feesBilledWTD.periodTotal && feesBilledWTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesBilledWTD.periodTotal)}` : null,
                    budget: feesBilledWTD.budget && feesBilledWTD.budget > 0 ? `${currencySymbol}${formatNumber(feesBilledWTD.budget)}` : null,
                    percentage: feesBilledWTD.percentage && feesBilledWTD.percentage > 0 ? `${formatNumber(feesBilledWTD.percentage)}%` : null,
                    dot: getIndicator(feesBilledWTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                }
            });

            // fees collected
            tempData.push({
                name: 'Fees Collected',
                monthToDate: {
                    actual: feesCollectedMTD.periodTotal && feesCollectedMTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesCollectedMTD.periodTotal)}` : null,
                    budget: feesCollectedMTD.budget && feesCollectedMTD.budget > 0 ? `${currencySymbol}${formatNumber(feesCollectedMTD.budget)}` : null,
                    percentage: feesCollectedMTD.percentage && feesCollectedMTD.percentage > 0 ? `${formatNumber(feesCollectedMTD.percentage)}%` : null,
                    dot: getIndicator(feesCollectedMTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                yearToDate: {
                    actual: feesCollectedYTD.periodTotal && feesCollectedYTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesCollectedYTD.periodTotal)}` : null,
                    budget: feesCollectedYTD.budget && feesCollectedYTD.budget > 0 ? `${currencySymbol}${formatNumber(feesCollectedYTD.budget)}` : null,
                    percentage: feesCollectedYTD.percentage && feesCollectedYTD.percentage > 0 ? `${formatNumber(feesCollectedYTD.percentage)}%` : null,
                    dot: getIndicator(feesCollectedYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                previousMonthToDate: {
                    actual:
                        feesCollectedPreviousMTD.periodTotal && feesCollectedPreviousMTD.periodTotal > 0
                            ? `${currencySymbol}${formatNumber(feesCollectedPreviousMTD.periodTotal)}`
                            : null,
                    budget: feesCollectedPreviousMTD.budget && feesCollectedPreviousMTD.budget > 0 ? `${currencySymbol}${formatNumber(feesCollectedPreviousMTD.budget)}` : null,
                    percentage: feesCollectedPreviousMTD.percentage && feesCollectedPreviousMTD.percentage > 0 ? `${formatNumber(feesCollectedPreviousMTD.percentage)}%` : null,
                    dot: getIndicator(feesCollectedPreviousMTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                previousMonthYearToDate: {
                    actual:
                        feesCollectedPreviousMYTD.periodTotal && feesCollectedPreviousMYTD.periodTotal > 0
                            ? `${currencySymbol}${formatNumber(feesCollectedPreviousMYTD.periodTotal)}`
                            : null,
                    budget: feesCollectedPreviousMYTD.budget && feesCollectedPreviousMYTD.budget > 0 ? `${currencySymbol}${formatNumber(feesCollectedPreviousMYTD.budget)}` : null,
                    percentage: feesCollectedPreviousMYTD.percentage && feesCollectedPreviousMYTD.percentage > 0 ? `${formatNumber(feesCollectedPreviousMYTD.percentage)}%` : null,
                    dot: getIndicator(feesCollectedPreviousMYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                forecast: {
                    actual: feesCollectedForecast.periodTotal && feesCollectedForecast.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesCollectedForecast.periodTotal)}` : null,
                    budget: feesCollectedForecast.budget && feesCollectedForecast.budget > 0 ? `${currencySymbol}${formatNumber(feesCollectedForecast.budget)}` : null,
                    percentage: feesCollectedForecast.percentage && feesCollectedForecast.percentage > 0 ? `${formatNumber(feesCollectedForecast.percentage)}%` : null,
                    dot: getIndicator(feesCollectedForecast.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                weekToDate: {
                    actual: feesCollectedWTD.periodTotal && feesCollectedWTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesCollectedWTD.periodTotal)}` : null,
                    budget: feesCollectedWTD.budget && feesCollectedWTD.budget > 0 ? `${currencySymbol}${formatNumber(feesCollectedWTD.budget)}` : null,
                    percentage: feesCollectedWTD.percentage && feesCollectedWTD.percentage > 0 ? `${formatNumber(feesCollectedWTD.percentage)}%` : null,
                    dot: getIndicator(feesCollectedWTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                }
            });

            // non-billable hours
            tempData.push({
                name: 'Non-Billable Hours',
                monthToDate: {
                    actual: nonBillableHoursMTD.periodTotal && nonBillableHoursMTD.periodTotal > 0 ? formatNumber(nonBillableHoursMTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                yearToDate: {
                    actual: nonBillableHoursYTD.periodTotal && nonBillableHoursYTD.periodTotal > 0 ? formatNumber(nonBillableHoursYTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                previousMonthToDate: {
                    actual: nonBillableHoursPreviousMTD.periodTotal && nonBillableHoursPreviousMTD.periodTotal > 0 ? formatNumber(nonBillableHoursPreviousMTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                previousMonthYearToDate: {
                    actual:
                        nonBillableHoursPreviousMYTD.periodTotal && nonBillableHoursPreviousMYTD.periodTotal > 0 ? formatNumber(nonBillableHoursPreviousMYTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                forecast: {
                    actual: nonBillableHoursYTD.periodTotal && nonBillableHoursYTD.periodTotal > 0 ? formatNumber(nonBillableHoursYTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                weekToDate: {
                    actual: nonBillableHoursWTD.periodTotal && nonBillableHoursWTD.periodTotal > 0 ? formatNumber(nonBillableHoursWTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                }
            });

            setData(tempData);
            setBottomData(tempBottomData);
        }
    // tslint:disable-next-line: align
    }, [kpiSummaryQuery.loading, kpiSummaryQuery.error, kpiSummaryQuery.data, currencySymbol]);

    const onToggleVideo = () => {
        setIsVideoModalOpen(!isVideoModalOpen);
    };

    const onMaximize = () => {
        if (props.onMaximize) {
            props.onMaximize();
        }
    };

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart(`isKpiSummaryOpen`);
        }
    };

    const onDownload =
        (name: string, extension: string = 'jpg') =>
        // tslint:disable-next-line: no-any
        (image: any) => {
            const a = document.createElement('a');
            a.href = image;
            a.download = createFileName(extension, name);
            a.click();
        };

    // tslint:disable-next-line: no-any
    const onDownloadScreenshot = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
        setTimeout(() => {
            takeScreenShot(entireChartRef.current).then(onDownload(`KPI Summary`));
            // tslint:disable-next-line: align
        }, 500);
    };

    const onAIGeneratedSuggestion = async (event: any) => {

        setIsBlobGenerating(true);

        event.preventDefault();
        event.stopPropagation();
       
        if (entireChartRef && entireChartRef.current) {
            const canvas = await html2canvas(entireChartRef.current);
            canvas.toBlob((blob) => {
                if (blob) {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = async () => {
                        const tempBase64data = reader.result;
                        // console.log(tempBase64data); // This is your image blob string
                        setBase64Data(tempBase64data);
                        setIsAIDialogOpen(true);
                    };
                }
            });
        }

        setIsBlobGenerating(false);
    };

    const getPeriodTitle = (period: DateOption) => {
        switch (period) {
            case 'monthToDate':
                return 'Month to Date';
            case 'previousMonthToDate':
                return 'Previous Month';
            case 'yearToDate':
                return 'Year to Date';
            case 'previousMonthYearToDate':
                return 'Previous Month YTD';
            case 'forecast':
                return 'Full Year Budget Forecast';
            case 'weekToDate':
                return 'Week to Date';
            default:
                return 'Month to Date';
        }
    };

    const getNewMattersCriteria = (period: DateOption): SearchCriteria[] => {
        switch (period) {
            case 'monthToDate':
                return [{
                    comparison: 'curr',
                    operator: 'instruction',
                    property: 'MonthToDate',
                }];
            case 'previousMonthToDate':
                return [{
                    comparison: 'prev',
                    operator: 'instruction',
                    property: 'ThisMonth',
                }];
            case 'yearToDate':
                return [{
                    comparison: 'curr',
                    operator: 'instruction',
                    property: 'YearToDate',
                }];
            case 'previousMonthYearToDate':
                return [{
                    comparison: 'pmytd',
                    operator: 'instruction',
                    property: 'MonthToDate',
                }];
            case 'forecast':
                return [{
                    comparison: 'curr',
                    operator: 'instruction',
                    property: 'YearToDate',
                }];
            case 'weekToDate':
                return [{
                    comparison: 'curr',
                    operator: 'instruction',
                    property: 'WeekToDate',
                }];
            default:
                return [{
                    comparison: 'curr',
                    operator: 'instruction',
                    property: 'MonthToDate',
                }];
        }
    };

    const getNonBillableHoursCriteria = (period: DateOption): SearchCriteria[] => {
        switch (period) {
            case 'monthToDate':
                return [
                    { property: 'ThisMonth', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'eq', comparison: '0' }
                ];
            case 'previousMonthToDate':
                return [
                    {property: 'Amount', operator: 'eq', comparison: '0'},
                    {property: 'ThisMonth', operator: '', comparison: 'prev'}
                ];
            case 'yearToDate':
                return [
                    { property: 'FinancialYear', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'eq', comparison: '0' }
                ];
            case 'previousMonthYearToDate':
                return [
                    {property: 'Amount', operator: 'eq', comparison: '0'},
                    {property: 'FinancialYear', operator: '', comparison: 'prevMonth'}
                ];
            case 'forecast':
                return [
                    { property: 'FinancialYear', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'eq', comparison: '0' }
                ];
            case 'weekToDate':
                return [
                    { property: 'WeekToDate', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'eq', comparison: '0' }
                ];
            default:
                return [
                    { property: 'ThisMonth', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'eq', comparison: '0' }
                ];
        }
    };

    const getBillableHoursCriteria = (period: DateOption): SearchCriteria[] => {
        switch (period) {
            case 'monthToDate':
                return [
                    { property: 'MonthToDate', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'gt', comparison: '0' }
                ];
            case 'previousMonthToDate':
                return [{ property: 'MonthToDate', operator: '', comparison: 'prev'}, { property: 'Amount', operator: 'gt', comparison: '0'}];
            case 'yearToDate':
                return [
                    { property: 'FinancialYear', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'gt', comparison: '0' }
                ];
            case 'previousMonthYearToDate':
                return [{property: 'FinancialYear', operator: '', comparison: 'prevMonth'}, { property: 'Amount', operator: 'gt', comparison: '0'}];
            case 'forecast':
                return [
                    { property: 'FinancialYear', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'gt', comparison: '0' }
                ];
            case 'weekToDate':
                return [
                    { property: 'WeekToDate', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'gt', comparison: '0' }
                ];
            default:
                return [
                    { property: 'MonthToDate', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'gt', comparison: '0' }
                ];
        }
    };

    const getFeesRecordedCriteria = (period: DateOption): SearchCriteria[] => {
        switch (period) {
            case 'monthToDate':
                return [{ property: 'Month', operator: '', comparison: 'curr', period: '' }];
            case 'previousMonthToDate':
                return [{property: 'Month', operator: '', comparison: 'prev', period: ''}];
            case 'yearToDate':
                return [{ property: 'FinancialYear', operator: '', comparison: 'curr', period: '' }];
            case 'previousMonthYearToDate':
                return [{property: 'FinancialYear', operator: '', comparison: 'curr', period: ''}];
            case 'forecast':
                return [{ property: 'FullFinancialYear', operator: '', comparison: '' }];
            case 'weekToDate':
                return [{ property: 'Week', operator: '', comparison: 'curr', period: '' }];
            default:
                return [{ property: 'Month', operator: '', comparison: 'curr', period: '' }];
        }
    };

    const getFeesBilledCriteria = (period: DateOption): SearchCriteria[] => {
        switch (period) {
            case 'monthToDate':
                return [{ property: 'Month', operator: '', comparison: '', period: 'curr' }];
            case 'previousMonthToDate':
                return [{property: 'Month', operator: '', comparison: '', period: 'prev'}];
            case 'yearToDate':
                return [{ property: 'FinancialYearToDate', operator: '', comparison: '', period: 'curr' }];
            case 'previousMonthYearToDate':
                return [{property: 'FinancialYearToDate', operator: '', comparison: '', period: 'curr'}];
            case 'forecast':
                return [{ property: 'FullFinancialYear', operator: '', comparison: '' }];
            case 'weekToDate':
                return [{ property: 'Week', operator: '', comparison: '', period: 'curr' }];
            default:
                return [{ property: 'Month', operator: '', comparison: '', period: 'curr' }];
        }
    };

    const getFeesCollectedCriteria = (period: DateOption): SearchCriteria[] => {
        switch (period) {
            case 'monthToDate':
                return [{ property: 'Month', operator: '', comparison: '', period: 'curr' }];
            case 'previousMonthToDate':
                return [{property: 'Month', operator: '', comparison: '', period: 'prev'}];
            case 'yearToDate':
                return [{ property: 'FinancialYearToDate', operator: '', comparison: '', period: 'curr' }];
            case 'previousMonthYearToDate':
                return [{property: 'FinancialYearToDate', operator: '', comparison: '', period: 'curr'}];
            case 'forecast':
                return [{ property: 'FullFinancialYear', operator: '', comparison: '' }];
            case 'weekToDate':
                return [{ property: 'Week', operator: '', comparison: '', period: 'curr' }];
            default:
                return [{ property: 'Month', operator: '', comparison: '', period: 'curr' }];
        }
    };

    const onTotalDrilldown = (item: KPISummaryBottomData) => (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            let title = `KPI Summary - ${item.name}`;
            let drillDownType = -1;
            let defaultView = '';

            if (item.name === 'Total Debt') {
                searchCriterias.push({property: 'DebtOwing', operator: 'not', comparison: '0'});
                searchCriterias.push({property: 'DebtOwing', operator: 'isnot', comparison: 'null'});
                drillDownType = DrillDownType.matterSummaryDrilldown;
                defaultView = 'Debtors';
            } else if (item.name === 'Unbilled Fees') {
                searchCriterias.push({property: 'TotalWIPBalanceExTax', operator: 'not', comparison: '0'});
                searchCriterias.push({property: 'TotalWIPBalanceExTax', operator: 'isnot', comparison: 'null'});
                drillDownType = DrillDownType.matterSummaryDrilldown;
                defaultView = 'Unbilled Fees';
            } else if (item.name === 'Unbilled Disbursements') {
                searchCriterias.push({property: 'UnbilledDisbursements', operator: 'gt', comparison: '0'});
                drillDownType = DrillDownType.matterSummaryDrilldown;
                defaultView = 'Matters';
            }

            props.getSelectedItem(searchCriterias, true, title, drillDownType, defaultView);
        }
    };

    const onDrilldown = (name: KPISummaryDataType, period: DateOption, type: keyof KPISummaryToDateType) => (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            let title = '';
            let drillDownType = -1;
            let defaultView = '';
            
            if (name === 'New Matters') {
                // title = `KPI Summary - New Matters ${period === 'YearToDate' ? 'YTD' : 'MTD'} ${capitalizeFirstLetter(type)}`;
                title = `KPI Summary - New Matters for ${getPeriodTitle(period)}`;

                searchCriterias.push(...getNewMattersCriteria(period));
                
                drillDownType = DrillDownType.matterSummaryDrilldown;

                defaultView = 'New Matter Details';

            } else if (name === 'Non-Billable Hours') {
                // title = `KPI Summary - Non-Billable Hours ${period === 'YearToDate' ? 'YTD' : 'MTD'} ${capitalizeFirstLetter(type)}`;
                title = `Drilldown from KPI Summary - Non-Billable for ${getPeriodTitle(period)}`;
                searchCriterias.push(...getNonBillableHoursCriteria(period));
                drillDownType = DrillDownType.timesheetSummary;
                defaultView = 'Timesheet Lines';

            } else if (name === 'Billable Hours') {
                // title = `KPI Summary - Billable Hours ${period === 'YearToDate' ? 'YTD' : 'MTD'} ${capitalizeFirstLetter(type)}`;
                title = `Drilldown from KPI Summary - Billable Hours for ${getPeriodTitle(period)}`;
                searchCriterias.push(...getBillableHoursCriteria(period));
                drillDownType = DrillDownType.timesheetSummary;
                defaultView = 'Timesheet Lines';

            } else if (name === 'Fees Recorded') {
                // title = `KPI Summary - Fees Recorded ${period === 'YearToDate' ? 'YTD' : 'MTD'} ${capitalizeFirstLetter(type)}`;
                title = `Drilldown from KPI Summary - Fees Recorded for ${getPeriodTitle(period)}`;
                searchCriterias.push(...getFeesRecordedCriteria(period));
                drillDownType = DrillDownType.staffBillingSummary;
                switch (period) {
                    case 'weekToDate':
                        defaultView = 'Fees Produced WTD';
                        break;
                    case 'monthToDate':
                    case 'yearToDate':
                        defaultView = 'Fees Produced MTD/YTD';
                        break;
                    case 'forecast':
                        defaultView = 'Budget Forecast';
                        break;
                    default:
                        defaultView = 'Fees Produced PM/PYTD';
                        break;
                }

            } else if (name === 'Fees Billed') {
                // title = `KPI Summary - Fees Billed ${period === 'YearToDate' ? 'YTD' : 'MTD'} ${capitalizeFirstLetter(type)}`;
                title = `Drilldown from KPI Summary - Fees Billed for ${getPeriodTitle(period)}`;
                searchCriterias.push(...getFeesBilledCriteria(period));
                drillDownType = DrillDownType.staffBillingSummary;
                switch (period) {
                    case 'weekToDate':
                        defaultView = 'Fees Billed WTD';
                        break;
                    case 'monthToDate':
                    case 'yearToDate':
                        defaultView = 'Fees Billed MTD/YTD';
                        break;
                    case 'forecast':
                        defaultView = 'Budget Forecast';
                        break;
                    default:
                        defaultView = 'Fees Billed PM/PYTD';
                        break;
                }

            } else if (name === 'Fees Collected') {
                // title = `KPI Summary - Fees Collected ${period === 'YearToDate' ? 'YTD' : 'MTD'} ${capitalizeFirstLetter(type)}`;
                title = `Drilldown from KPI Summary - Fees Collected for ${getPeriodTitle(period)}`;
                searchCriterias.push(...getFeesCollectedCriteria(period));
                drillDownType = DrillDownType.staffBillingSummary;
                switch (period) {
                    case 'weekToDate':
                        defaultView = 'Fees Collected WTD';
                        break;
                    case 'monthToDate':
                    case 'yearToDate':
                        defaultView = 'Fees Collected MTD/YTD';
                        break;
                    case 'forecast':
                        defaultView = 'Budget Forecast';
                        break;
                    default:
                        defaultView = 'Fees Collected PM/PYTD';
                        break;
                }
            }
            props.getSelectedItem(searchCriterias, true, title, drillDownType, defaultView);
        }
        
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOptions((event.target as HTMLInputElement).value as DateOption);
    };

    const chartControlComponent = () => {
        return (
            <>
                <IconButtonWrapper aria-label="close">
                    <div className={`${classes.helpIcon} rv-chart-help`}>
                        <ChartInfo
                            description={props.description}
                            fontSize={props.showAllRecords ? 'large' : 'medium'}
                            // className={classes.helpIcon}
                        />
                        {props.infoUrl && (
                            <MTooltip title="Learn more">
                                <IconButton className={classes.learnIcon} aria-label="maximize" onClick={onToggleVideo} size={props.showAllRecords ? 'medium' : 'small'}>
                                    <VideoLibraryOutlinedIcon />
                                </IconButton>
                            </MTooltip>
                        )}
                    </div>
                    {props.isAIFeatureEnabled && (
                        <MTooltip title="AI Assistant">
                            <IconButton 
                                disabled={isBlobGenerating}
                                size={props.showAllRecords ? 'medium' : 'small'}
                                onClick={onAIGeneratedSuggestion}
                            >
                                <AIIcon 
                                    color="primary"
                                    fontSize={props.showAllRecords ? 'large' : 'medium'}
                                />
                            </IconButton>
                        </MTooltip>
                    )}
                    {(props.showAllRecords || props.showScreenshot) && (
                        <IconButton 
                            size={props.showAllRecords ? 'medium' : 'small'}
                            onClick={onDownloadScreenshot}
                        >
                            <GetAppOutlinedIcon 
                                color="primary" 
                                fontSize={props.showAllRecords ? 'large' : 'medium'}
                            />
                        </IconButton>
                    )}
                    {!props.showAllRecords && props.onMaximize && (
                        <MTooltip title="Expand chart">
                            <IconButton aria-label="maximize" onClick={onMaximize} size="small">
                                <AspectRatioIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                    <IconButton aria-label="close" onClick={onCloseChart} size={props.showAllRecords ? 'medium' : 'small'}>
                        <CloseIcon fontSize={props.showAllRecords ? 'large' : 'medium'} />
                    </IconButton>
                </IconButtonWrapper>
            </>
        );
    };

    if (kpiSummaryQuery.loading || currencyQuery.loading) {
        return (
            <div className={`${classes.rootLoader} ${RV_CHART_CLASS}`}>
                {chartControlComponent()}
                <ChartSkeleton variant="bars" />
            </div>
        );
    }

    const excludedForForecast = ['New Matters', 'Billable Hours', 'Non-Billable Hours'];

    // The figures are already written by the chart ("$47,281", "80%"); this counts the digits up inside the same text.
    const renderAnimatedFigure = (figure: number | string | null) => {
        if (figure === null || figure === undefined) {
            return null;
        }
        const parts = /^(\D*)(-?[\d,]+(?:\.\d+)?)(.*)$/.exec(String(figure));
        if (!parts) {
            return figure;
        }
        const prefix = parts[1];
        const suffix = parts[3];
        return <AnimatedNumber value={Number(parts[2].replace(/,/g, ''))} format={(amount) => `${prefix}${formatNumber(amount)}${suffix}`} />;
    };

    const getPercentageNumber = (percentage: number | string | null) => {
        const percentageNumber = Number(String(percentage ?? '').replace(/[^\d.-]/g, ''));
        return isNaN(percentageNumber) ? 0 : Math.min(100, Math.max(0, percentageNumber));
    };

    // The dot colour the chart already works out from the thresholds; no dot (no thresholds, or no budget) leaves the ring neutral.
    const getRingColor = (dot?: 'red' | 'orange' | 'green') => {
        if (dot === 'red') {
            return RV_CHART_COLORS.red;
        }
        if (dot === 'orange') {
            return RV_CHART_COLORS.amber;
        }
        if (dot === 'green') {
            return RV_CHART_COLORS.green;
        }
        return RV_CHART_COLORS.primary;
    };

    const ringRadius = 18;
    const ringLength = 2 * Math.PI * ringRadius;

    const visibleItems = data
        .filter((item) => options === 'forecast' ? !excludedForForecast.includes(item.name) : item)
        .filter((item) => {
            if (item.name === 'Fees Collected' || item.name === 'Non-Billable Hours') {
                return !!item[options]?.actual || !!item[options]?.budget;
            }
            return true;
        });

    const renderKpiTile = (item: KPISummaryData, index: number) => {
        const tempItem = item[options] ?? item.monthToDate;
        const ringColor = getRingColor(tempItem.dot);

        return (
            <div key={index} className={classes.tile}>
                {tempItem.percentage ? (
                    <div
                        className={clsx(classes.ring, tempItem.percentage ? classes.pointer : '')}
                        onClick={tempItem.percentage ? onDrilldown(item.name, options, 'percentage') : undefined}
                        title={tempItem.percentage ? `${tempItem.percentage}` : undefined}
                    >
                        <svg width="46" height="46" viewBox="0 0 46 46">
                            <circle cx="23" cy="23" r={ringRadius} fill="none" stroke="#f2f4fa" strokeWidth="5" />
                            <circle
                                className={classes.ringProgress}
                                cx="23"
                                cy="23"
                                r={ringRadius}
                                fill="none"
                                stroke={ringColor}
                                strokeWidth="5"
                                strokeLinecap="round"
                                strokeDasharray={ringLength}
                                strokeDashoffset={ringLength * (1 - getPercentageNumber(tempItem.percentage) / 100)}
                            />
                        </svg>
                        <div className={classes.ringLabel}>{renderAnimatedFigure(tempItem.percentage)}</div>
                    </div>
                ) : null}
                <div className={classes.tileText}>
                    <div className={classes.tileName}>{item.name}</div>
                    <div
                        className={clsx(classes.tileActual, tempItem.actual ? classes.pointer : '')}
                        onClick={tempItem.actual ? onDrilldown(item.name, options, 'actual') : undefined}
                    >
                        {tempItem.actual ? renderAnimatedFigure(tempItem.actual) : <span className={classes.tileEmpty}>–</span>}
                    </div>
                    <div
                        className={clsx(classes.tileBudget, tempItem.budget ? classes.pointer : '')}
                        onClick={tempItem.budget ? onDrilldown(item.name, options, 'budget') : undefined}
                    >
                        {tempItem.budget ? <>Budget {tempItem.budget}</> : null}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {isAIDialogOpen && (
                <AIChartDialog 
                    fullWidth={true}
                    maxWidth="md"
                    open={isAIDialogOpen}
                    onClose={() => setIsAIDialogOpen(false)}
                    data={base64Data}
                    query={props.aiPrompt || 'Give me recommendations on this and highlight any exceptions or concerns'}
                    accessSubType={chartName}
                    layoutId={props.layoutId}
                />
            )}

            <div className={`${classes.root} ${RV_CHART_CLASS}`} ref={entireChartRef}>
                {isVideoModalOpen && props.infoUrl && <VideoModal open={isVideoModalOpen} onClose={onToggleVideo} url={props.infoUrl} />}
                {chartControlComponent()}
                <div className={classes.titleContainer}>
                    <ChartHeading icon={<AssessmentOutlinedIcon />} title={chartName} />
                </div>
                <div className={classes.content}>
                    <div className={classes.eyebrow}>{`${options === 'forecast' ? 'Forecast' : 'Actual'} · Budget · % Achieved`}</div>
                    <div className={classes.tiles}>{visibleItems.map(renderKpiTile)}</div>
                <FormControl component="div">
                    <RadioGroup name="last" value={options} onChange={onChangeRadio} className={classes.radioGroup}>
                        <FormControlLabel
                            value="weekToDate"
                            control={<Radio color="primary" />}
                            label="Week to Date"
                        />
                        <FormControlLabel
                            value="monthToDate"
                            control={<Radio color="primary" />}
                            label="Month to Date"
                        />
                        <FormControlLabel
                            value="yearToDate"
                            control={<Radio color="primary" />}
                            label="Year to Date"
                        />
                        <FormControlLabel
                            value="previousMonthToDate"
                            control={<Radio color="primary" />}
                            label="Previous Month"
                        />                        
                        <FormControlLabel
                            value="previousMonthYearToDate"
                            control={<Radio color="primary" />}
                            label="Previous Month YTD"
                        />
                        <FormControlLabel
                            value="forecast"
                            control={<Radio color="primary" />}
                            label="Full Year Budget Forecast"
                        />
                    </RadioGroup>
                </FormControl>
                <div className={classes.eyebrow}>Debt &amp; unbilled</div>
                {bottomData.map((item, index) => {
                    const { data: tempBottomData, exceedPercentage } = item;
                    const isOverThreshold = !!(tempBottomData && exceedPercentage && Math.round(tempBottomData.ninetyDaysPercentage) >= exceedPercentage);
                    return (
                        <div
                            key={index}
                            className={clsx(classes.ageingRow, tempBottomData ? classes.pointer : '')}
                            onClick={onTotalDrilldown(item)}
                        >
                            <div className={classes.ageingTop}>
                                <span className={classes.ageingName}>{item.name}</span>
                                <span className={classes.ageingTotal}>
                                    <AnimatedNumber value={tempBottomData?.periodTotal ?? 0} format={(amount) => `${currencySymbol}${formatNumber(amount)}`} />
                                </span>
                            </div>
                            <div className={classes.ageingTrack}>
                                <div
                                    className={classes.ageingFill}
                                    style={{
                                        width: `${Math.min(100, Math.max(0, tempBottomData?.ninetyDaysPercentage ?? 0))}%`,
                                        backgroundColor: isOverThreshold ? RV_CHART_COLORS.red : RV_CHART_COLORS.primary,
                                    }}
                                />
                            </div>
                            <div className={classes.ageingText} style={isOverThreshold ? { color: 'red' } : undefined}>
                                {`(${currencySymbol}${formatNumber(tempBottomData?.ninetyDaysTotal ?? 0)} or ${formatNumber(tempBottomData?.ninetyDaysPercentage ?? 0)}% is 90 days old or greater)`}
                            </div>
                        </div>
                    );
                })}
                </div>
            </div>
        </>
    );    
};

type KpiSummaryDataParams = {
    entityType: number | undefined;
    entityGuid: string | undefined;
    responsibility: number | undefined;
};

type KpiSummaryData = {
    statistics: Statistics;
    settings: Settings;
};

type Settings = {
    id:         number;
    systems:    Systems;
    __typename: string;
}

type Systems = {
    financialThresholdsSettings: FinancialThresholdsSettings;
}

type FinancialThresholdsSettings = {
    id:         number;
    config:     Config;
}

type Config = {
    id:                                                              number | null;
    redIndicatorWhenActualVsBudgetIsLessThanPercentage:              number | null;
    amberIndicatorWhenActualVsBudgetIsLessThanPercentage:            number | null;
    redIndicatorWhenNinetyDayDebtExceedsPercentage:                  number | null;
    redIndicatorWhenNinetyDayUnbilledFeesExceedsPercentage:          number | null;
    redIndicatorWhenNinetyDayUnbilledDisbursementsExceedsPercentage: number | null;
    firmOverallStandardChargeOutRate:                                number | null;
    currentBankOverdraftInterestRate:                                number | null;
    firmStandardTradingTermsInDays:                                  number | null;
    firmTargetWipLockupDays:                                         number | null;
    showGreenIndicator:                                              boolean;
}

type Statistics = {
    fees: Fees;
};

type Fees = {
    kpiSummary: KpiSummary;
};

type KpiSummary = {
    newMattersYTD:               NewMattersMtd;
    newMattersMTD:               NewMattersMtd;
    newMattersWTD:               NewMattersMtd;
    newMattersPreviousMTD:       NewMattersMtd;
    newMattersPreviousMYTD:       NewMattersMtd;
    nonBillableHoursYTD:         NewMattersMtd;
    nonBillableHoursMTD:         NewMattersMtd;
    nonBillableHoursWTD:         NewMattersMtd;
    nonBillableHoursPreviousMTD: NewMattersMtd;
    nonBillableHoursPreviousMYTD: NewMattersMtd;
    billableHoursYTD:            BillableHoursMtd;
    billableHoursMTD:            BillableHoursMtd;
    billableHoursWTD:            BillableHoursMtd;
    billableHoursPreviousMTD:    BillableHoursMtd;
    billableHoursPreviousMYTD:    BillableHoursMtd;
    feesRecordedYTD:             BillableHoursMtd;
    feesRecordedMTD:             BillableHoursMtd;
    feesRecordedWTD:             BillableHoursMtd;
    feesRecordedPreviousMTD:     BillableHoursMtd;
    feesRecordedPreviousMYTD:     BillableHoursMtd;
    feesRecordedForecast:     BillableHoursMtd;
    feesBilledYTD:               BillableHoursMtd;
    feesBilledMTD:               BillableHoursMtd;
    feesBilledWTD:               BillableHoursMtd;
    feesBilledPreviousMTD:       BillableHoursMtd;
    feesBilledPreviousMYTD:       BillableHoursMtd;
    feesBilledForecast:       BillableHoursMtd;
    feesCollectedYTD:            BillableHoursMtd;
    feesCollectedMTD:            BillableHoursMtd;
    feesCollectedWTD:            BillableHoursMtd;
    feesCollectedPreviousMTD:    BillableHoursMtd;
    feesCollectedPreviousMYTD:    BillableHoursMtd;
    feesCollectedForecast:    BillableHoursMtd;
    totalDebtOwing:              Total | null;
    totalUnbilledFees:           Total | null;
    totalUnbilledDisbursements:  Total | null;
};

type BillableHoursMtd = {
    periodTotal: number;
    budget:      number;
    percentage:  number;
};

type NewMattersMtd = {
    periodTotal: number;
};

type Total = {
    periodTotal:          number;
    ninetyDaysPercentage: number;
    ninetyDaysTotal:      number;
};

export const KPISummaryQuery = gql`
    query kpiSummaryData($entityType: Int, $entityGuid: String, $responsibility: Int) {
        settings {
            id
            systems {
                financialThresholdsSettings {
                    id
                    config {
                        id
                        redIndicatorWhenActualVsBudgetIsLessThanPercentage
                        amberIndicatorWhenActualVsBudgetIsLessThanPercentage
                        redIndicatorWhenNinetyDayDebtExceedsPercentage
                        redIndicatorWhenNinetyDayUnbilledFeesExceedsPercentage
                        redIndicatorWhenNinetyDayUnbilledDisbursementsExceedsPercentage
                        firmOverallStandardChargeOutRate
                        currentBankOverdraftInterestRate
                        firmStandardTradingTermsInDays
                        firmTargetWipLockupDays
                        showGreenIndicator
                    }
                }
            }
        }
        statistics {
            fees {
                kpiSummary(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    newMattersYTD {
                        periodTotal
                    }
                    newMattersMTD {
                        periodTotal
                    }
                    newMattersWTD {
                        periodTotal
                    }
                    newMattersPreviousMTD {
                        periodTotal
                    }
                    newMattersPreviousMYTD {
                        periodTotal
                    }

                    nonBillableHoursYTD {
                        periodTotal
                    }
                    nonBillableHoursMTD {
                        periodTotal
                    }
                    nonBillableHoursWTD {
                        periodTotal
                    }
                    nonBillableHoursPreviousMTD {
                        periodTotal
                    }
                    nonBillableHoursPreviousMYTD {
                        periodTotal
                    }

                    billableHoursYTD {
                        periodTotal
                        budget
                        percentage
                    }
                    billableHoursMTD {
                        periodTotal
                        budget
                        percentage
                    }
                    billableHoursWTD {
                        periodTotal
                        budget
                        percentage
                    }
                    billableHoursPreviousMTD {
                        periodTotal
                        budget
                        percentage
                    }
                    billableHoursPreviousMYTD {
                        periodTotal
                        budget
                        percentage
                    }

                    feesRecordedYTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesRecordedMTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesRecordedWTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesRecordedPreviousMTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesRecordedPreviousMYTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesRecordedForecast {
                        periodTotal
                        budget
                        percentage
                    }

                    feesBilledYTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesBilledMTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesBilledWTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesBilledPreviousMTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesBilledPreviousMYTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesBilledForecast {
                        periodTotal
                        budget
                        percentage
                    }
                    feesCollectedYTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesCollectedMTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesCollectedWTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesCollectedPreviousMTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesCollectedPreviousMYTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesCollectedForecast {
                        periodTotal
                        budget
                        percentage
                    }
                    totalDebtOwing {
                        periodTotal
                        ninetyDaysPercentage
                        ninetyDaysTotal
                    }
                    totalUnbilledFees {
                        periodTotal
                        ninetyDaysPercentage
                        ninetyDaysTotal
                    }
                    totalUnbilledDisbursements {
                        periodTotal
                        ninetyDaysPercentage
                        ninetyDaysTotal
                    }
                }
            }
        }
    }
`;
